import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

// hooks
import { useHistory, useLocation } from "react-router-dom";
import useListSelector from "../../lib/useListSelector";

// components
import Sallie from "../UIComponents/Sallie";
import StrokeListSelecter from "../UIComponents/StrokeListSelecter";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";

// api
import { updateStrokeDetail } from "../../api/TBIRequests";

// lib
import customizeMessages from "../../lib/customizeMessages";
import { Form } from "react-bootstrap";

const RecentStroke2 = ({ setOnboardingPercent, user }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [returnedData, setReturnedData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const categories = [
    {
      category: "typeStroke",
      items: Array.from({ length: 6 }, (_, i) => t(`recentstroke2.type.options.${i}`)),
    },
    {
      category: "priorStroke",
      items: Array.from({ length: 13 }, (_, i) => t(`recentstroke2.prior.options.${i}`)),
    },
    {
      category: "riskFactors",
      items: Array.from({ length: 8 }, (_, i) => t(`recentstroke2.risks.options.${i}`)),
    },
  ];

  const strokeInfo = categories.map(({ category, items }) => ({ category, items }));

  const {
    toggleFromSelectedList,
    switchSingleSelection,
    addOther,
    selectedData,
    initialize,
  } = useListSelector({
    setInitialData: setReturnedData,
    initialData: returnedData,
  });

  const onSubmit = async () => {
    const assignedData = {
      typeStroke: selectedData.typeStroke[0],
      priorStroke: selectedData.priorStroke,
      riskFactors: selectedData.riskFactors,
    };
    if (
      !assignedData.typeStroke ||
      assignedData.priorStroke.length === 0 ||
      assignedData.riskFactors.length === 0
    ) {
      setErrorMessage(true);
      return;
    }
    setSubmitting(true);
    try {
      const tbiDetailRes = await updateStrokeDetail({
        patientId: patient.patientId,
        data: assignedData,
      });
      history.push("/laterSymptoms", {
        date: tbiDetailRes.data.slice(0, tbiDetailRes.data.indexOf("T")),
        fromPatientControl: location.state?.fromPatientControl,
      });
    } catch (err) {
      history.push("/oops");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(40)
      : setOnboardingPercent(30);
    setReturnedData(strokeInfo);
    initialize(strokeInfo);
    setLoading(false);
  }, [t]);

  const getPossibleMessages = () => ({
    mainHeader: {
      Stroke: t("recentstroke2.header"),
      caregiver: t("recentstroke2.header"),
    },
    sallieText: {
      Stroke: t("recentstroke2.sallie.patient"),
      caregiver: t("recentstroke2.sallie.caregiver"),
    },
    typeStroke: {
      Stroke: t("recentstroke2.type.patient"),
      caregiver: t("recentstroke2.type.caregiver"),
    },
    priorStroke: {
      Stroke: t("recentstroke2.prior.patient"),
      caregiver: t("recentstroke2.prior.caregiver"),
    },
    riskFactors: {
      Stroke: t("recentstroke2.risks.patient"),
      caregiver: t("recentstroke2.risks.caregiver"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  const options = {
    typeStroke: { message: "typeStroke", responseNumber: 1 },
    priorStroke: { message: "priorStroke" },
    riskFactors: { message: "riskFactors" },
  };

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <div className="d-block">
          <OnboardingStepHeader text={getMessage("mainHeader")} />
          <Sallie text={getMessage("sallieText")} />

          {returnedData.map((itemList, i) => {
            const { category } = itemList;
            return (
              <StrokeListSelecter
                key={category}
                category={category}
                message={getMessage(options[category].message)}
                list={itemList.items}
                index={i}
                maxSelectAmount={options[category].responseNumber}
                selectedList={selectedData[category]}
                toggleFromSelectedList={toggleFromSelectedList}
                selectOne={switchSingleSelection}
                addOther={addOther}
              />
            );
          })}
          {errorMessage && (
            <Form.Control.Feedback style={{ display: "block" }} type="invalid">
              {t("recentstroke2.choose.error")}
            </Form.Control.Feedback>
          )}
          <SubmitButton disabled={submitting} onClick={onSubmit} type="submit">
            {t("onboarding.saveContinue")} &gt;
          </SubmitButton>
        </div>
      )}
    </>
  );
};

export default RecentStroke2;